<template>
  <div class="ac-chips" role="tablist" aria-label="Alert types">
    <button
      v-for="c in categories"
      :key="c.key"
      type="button"
      role="tab"
      class="ac-chip"
      :class="{ 'is-active': c.key === active }"
      :aria-selected="c.key === active ? 'true' : 'false'"
      :data-test="`ac-chip-${c.key}`"
      @click="select(c.key, $event)"
    >
      <AcIcon :name="c.icon" :size="14" />
      <span class="ac-chip-label">{{ c.label }}</span>
      <span class="ac-chip-count">{{ c.count }}</span>
    </button>
  </div>
</template>

<script>
import AcIcon from './AcIcon.vue';

// All · SMS · Forms · Tx · Appts · Payments, each with its live count. `categories` comes from
// actionCenterAlerts.CATEGORIES merged with countByCategory.
export default {
  name: 'ActionCenterChips',
  components: { AcIcon },
  props: {
    categories: { type: Array, required: true },
    active: { type: String, default: 'all' },
  },
  methods: {
    select(key, event) {
      this.$emit('select', key);
      // Phones scroll the row sideways: keep the chosen chip in view.
      const el = event && event.currentTarget;
      if (el && typeof el.scrollIntoView === 'function') {
        try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) { /* old browsers */ }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ac-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 12px 14px 10px;
}

.ac-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 11px;
  border-radius: 999px;
  border: 1px solid var(--ac-border);
  background: var(--ac-surface);
  color: var(--ac-neutral-600);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(.is-active) {
    border-color: var(--ac-border-strong);
    color: var(--ac-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(91, 91, 214, 0.3);
  }

  &.is-active {
    background: var(--ac-iris-600);
    border-color: var(--ac-iris-600);
    color: #fff;
  }
}

.ac-chip-count {
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 999px;
  background: var(--ac-neutral-100);
  color: var(--ac-neutral-600);

  .is-active & {
    background: rgba(255, 255, 255, 0.24);
    color: #fff;
  }
}

// Phones: one scrollable row with snap and 44px targets instead of wrapping.
@media (max-width: 767.98px) {
  .ac-chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .ac-chip {
    height: 44px;
    padding: 0 14px;
    scroll-snap-align: start;
  }
}
</style>
